import { defaultTo } from 'aa_common/front-end/helpers/common'
import { useNextEffect } from 'common/hooks/use-next-effect'
import { PresenterPreFillDraftAssetResponse } from 'common/open-api'
import { useFormikContext } from 'formik'
import isEqual from 'lodash/isEqual'
import { useSelector } from 'react-redux'
import { getExciseSettingsByDateList } from 'store/settings/excise/selectors'

import { getExciseInputSetting } from '../helper'
import { setAcquisitionCost } from '../helpers/set-acquisition-cost'

/**
 * Custom hook to set form values based on a calculated draft fixed asset response.
 * This hook should be the last hook called in the component to ensure that it runs after all other hooks have been executed.
 * @param calculatedDraftFA - The calculated draft fixed asset response.
 */

export const useSetFromCalculatedDraft = (calculatedDraftFA?: PresenterPreFillDraftAssetResponse) => {
  const { setFieldValue, getFieldProps } = useFormikContext()
  const { data: exciseSetting } = useSelector(getExciseSettingsByDateList, isEqual)

  useNextEffect(() => {
    const { isExciseSettingAvailable, isInputTaxWhenRegister, isTaxMethodExcluded, isInternalTax, isShowTax } =
      getExciseInputSetting(exciseSetting)

    if (!isExciseSettingAvailable || !calculatedDraftFA) {
      return
    }

    const acquisitionCostExcluded = defaultTo(calculatedDraftFA?.acquisition_cost_excluded_excise, 0)
    const exciseValue = defaultTo(calculatedDraftFA?.excise_value, 0)
    let acquisitionCostInput = acquisitionCostExcluded
    if (isInputTaxWhenRegister && (!isTaxMethodExcluded || isInternalTax)) {
      acquisitionCostInput = acquisitionCostExcluded + exciseValue
    }

    setFieldValue('acquisition_cost_input', acquisitionCostInput)
    setAcquisitionCost(
      acquisitionCostInput,
      exciseValue,
      { isInputTaxWhenRegister, isTaxMethodExcluded, isInternalTax },
      setFieldValue
    )
    if (isInputTaxWhenRegister) {
      setFieldValue('excise_id', calculatedDraftFA?.excise_id)
    }
    if (isShowTax) {
      setFieldValue('manual_excise_value', exciseValue)
    }
    if (!getFieldProps('department_biid')?.value && calculatedDraftFA?.department_biid) {
      setFieldValue('department_biid', {
        value: calculatedDraftFA?.department_biid,
        label: calculatedDraftFA?.department_name ?? '',
      })
    }
    if (!getFieldProps('urls')?.value && calculatedDraftFA?.urls?.length) {
      setFieldValue('urls', calculatedDraftFA?.urls)
    }
    if (!getFieldProps('memo')?.value) {
      setFieldValue('memo', calculatedDraftFA?.memo)
    }
  }, [exciseSetting, calculatedDraftFA])
}
